// Sound (docs/plans/2026-10-17-asset-phases.md, Phase 2 and the sound half of Phase 3). WebAudio, unlocked on the first
// key, click or touch; four buses (ui, sfx, crowd, music) into a master. Sound effects are preloaded; music streams
// once a round starts. Cues come from successive Chalo snapshots (cuesFor, pure, so it is tested without a browser).
// Files live flat in public/audio/ (README.md there says where each came from). A missing file plays nothing.
import type { ChaloRules, ChaloSnap, ChaloGeo } from '../shared/chalo';
import type { Vec } from '../shared/map';
import { DT } from '../shared/sim';
import { FONT, INK, box } from './ui';

// ------------------------------------------------------------------ the sounds

export type Bus = 'ui' | 'sfx' | 'crowd' | 'music';
interface SoundDef { files: string[]; bus: Bus; gain: number; cap: number; rate?: number; lazy?: boolean }
/** Every sound effect by id: its files (one is picked at random), bus, gain, and how many may play at once. */
export const SOUNDS = {
  whistleShort: { files: ['police-whistle-short'], bus: 'sfx', gain: 0.8, cap: 2 },
  whistleLong: { files: ['police-whistle-long'], bus: 'sfx', gain: 0.75, cap: 2 },
  shutter: { files: ['shutter-close'], bus: 'sfx', gain: 0.9, cap: 2 },
  garble: { files: ['loudspeaker-garble'], bus: 'sfx', gain: 0.8, cap: 1 }, // speech-like on purpose, with no words
  impact: { files: ['impact-metal-light-0', 'impact-metal-light-1'], bus: 'sfx', gain: 0.3, cap: 3 }, // a police line slides: low
  dhol: { files: ['dhol-loop-100bpm'], bus: 'sfx', gain: 0.9, cap: 1 },
  clap: { files: ['crowd-clap-loop-100bpm'], bus: 'crowd', gain: 0.7, cap: 1 },
  crowd: { files: ['crowd-shouting-ambience'], bus: 'crowd', gain: 0.5, cap: 1 },
  sting: { files: ['sting-ring'], bus: 'ui', gain: 0.7, cap: 1 },
  camera: { files: ['camera-shutter'], bus: 'sfx', gain: 0.8, cap: 2 },
  siren: { files: ['siren'], bus: 'sfx', gain: 0.35, cap: 1 }, // distant: the bus leaving with detainees
  applause: { files: ['applause-hall'], bus: 'ui', gain: 0.7, cap: 1 },
  win: { files: ['jingle-win'], bus: 'ui', gain: 0.8, cap: 1 },
  lose: { files: ['jingle-lose'], bus: 'ui', gain: 0.7, cap: 1, rate: 0.84 }, // played a little slower: lower
  click: { files: ['ui-click'], bus: 'ui', gain: 0.5, cap: 3 },
  confirm: { files: ['ui-confirm'], bus: 'ui', gain: 0.6, cap: 1 },
  toggle: { files: ['ui-switch'], bus: 'ui', gain: 0.5, cap: 1 },
  // Phase 5: recordings nobody had for free. Fetched the first time they are wanted; until the file exists, silence
  chant: { files: ['chant-loop'], bus: 'crowd', gain: 0.6, cap: 1, lazy: true },
  busEngine: { files: ['bus-engine'], bus: 'sfx', gain: 0.5, cap: 1, lazy: true },
  // the platform's background: trains coming in and pulling out on the other platform while you wait (main.ts, platformTrains)
  trainIn: { files: ['metro-train-in'], bus: 'sfx', gain: 0.55, cap: 1 },
  trainOut: { files: ['metro-train-out'], bus: 'sfx', gain: 0.55, cap: 1 },
  // through the metro gate, from the street down to the platform (main.ts): the gate's entry alert, once
  gateEntry: { files: ['metro-gate-entry'], bus: 'sfx', gain: 0.7, cap: 1 },
  // the title screen (tools/make-title-sounds.py): the camera sliding to the next place, and a stamp coming down
  slide: { files: ['title-slide'], bus: 'ui', gain: 0.45, cap: 1 },
  stamp: { files: ['title-stamp'], bus: 'ui', gain: 0.7, cap: 2 },
} satisfies Record<string, SoundDef>;
export type SoundId = keyof typeof SOUNDS;
/** Music, streamed (never preloaded). */
export const MUSIC = {
  gather: 'music-a-newcomer-main-layer', gatherExtra: 'music-a-newcomer-extra-layer',
  march: 'music-electronic-tabla', debrief: 'music-mystical-enigmatic',
  // the platform's bed (platformBed): the station's own hum and air, made here with ffmpeg
  platformAir: 'music-platform-ambience',
  // the title's city under its theme (tools/make-title-sounds.py): traffic, horns, the crowd far off
  titleCity: 'music-title-city',
} as const;
/** The platform's bed while you wait: the station's air at this level, and the calmest track softly under the PA. */
const PLATFORM_BED: [MusicId, number][] = [['platformAir', 0.9], ['debrief', 0.22]];
/** The title's bed: the theme's two layers, and the city under them. */
const TITLE_BED: [MusicId, number][] = [['gather', 0.42], ['gatherExtra', 0.22], ['titleCity', 0.85]];
export type MusicId = keyof typeof MUSIC;

/** Every file the code names, and the ones preloaded (all the non-lazy sound effects). */
export const PRELOAD_FILES = Object.values(SOUNDS as Record<string, SoundDef>).filter((d) => !d.lazy).flatMap((d) => d.files);
export const OPTIONAL_FILES = Object.values(SOUNDS as Record<string, SoundDef>).filter((d) => d.lazy).flatMap((d) => d.files);
export const MUSIC_FILES = Object.values(MUSIC) as string[];

// ------------------------------------------------------------------ maths

/** The snapshot radius: a sound further than this is silent. */
export const AUDIBLE_M = 130;
/** Gain for a sound d metres away: (1 − d/130)², never below 0. */
export function posGain(dM: number): number { const k = 1 - dM / AUDIBLE_M; return k > 0 ? k * k : 0; }
/** Pan from where the sound is on screen: −1 at the left edge, +1 at the right, clamped. */
export function panFor(sx: number, W: number): number { return W > 0 ? Math.max(-1, Math.min(1, (sx - W / 2) / (W / 2))) : 0; }

/** The Rassakashi beat: 100 bpm. */
export const BEAT_S = 0.6;
/** The first beat at least `lead` seconds after Chalo time T: its index and time (beat k is at k·0.6). */
export function nextBeat(T: number, lead = 0): { k: number; at: number } {
  const k = Math.max(0, Math.ceil((T + lead) / BEAT_S - 1e-9));
  return { k, at: k * BEAT_S };
}
/** Where in a loop of `beats` beats to start so that beat k of the round lands on the loop's beat k mod beats. */
export const loopOffset = (k: number, beats: number) => (beats > 0 ? (k % beats) * BEAT_S : 0);

// the sim adds DT to its Chalo clock each tick and sends it rounded to 0.1 s; replaying the same float sums tells which
// tick the clock started on, so the clock is known to the tick (50 ms) instead of to the rounding (±50 ms)
const acc: number[] = [0];
/** The `t` the sim sends after n ticks of the Chalo clock. */
export function chaloT(n: number): number {
  while (acc.length <= n) acc.push(acc[acc.length - 1] + DT);
  return Math.round(acc[n] * 10) / 10;
}

/**
 * The Chalo clock as it arrives here: snapshots give (tick, t) and the local time they came in (performance seconds).
 * at(p) is the Chalo time the snapshot stream shows at local time p (the server's clock, one network trip late: a
 * press made on a beat heard on this clock reaches the server on that beat). Arrival delays only ever add, so the
 * offset is the smallest seen in the last 2 s of ticks.
 */
export class ChaloClock {
  tk0: number | null = null;
  private cands: number[] = [];
  private offs: number[] = [];
  private lastTk = -1; private lastT = -1;
  push(tk: number, t: number, p: number) {
    const fresh = tk <= this.lastTk || t < this.lastT;
    this.lastTk = tk; this.lastT = t;
    if (fresh || !this.cands.length) this.reset();
    const base = tk - Math.round(t / DT);
    if (!this.cands.length) this.cands = [base - 1, base, base + 1];
    this.cands = this.cands.filter((c) => tk - c >= 0 && chaloT(tk - c) === t);
    if (!this.cands.length) { this.reset(); this.cands = [base - 1, base, base + 1].filter((c) => tk - c >= 0 && chaloT(tk - c) === t); }
    if (!this.cands.length) return;
    const c = this.cands.includes(base) ? base : this.cands[0];
    if (c !== this.tk0) { this.tk0 = c; this.offs = []; }
    this.offs.push(p - (tk - c) * DT);
    if (this.offs.length > 40) this.offs.shift();
  }
  private reset() { this.cands = []; this.offs = []; this.tk0 = null; }
  get synced() { return this.offs.length >= 4; }
  private get off() { return Math.min(...this.offs); }
  /** Chalo seconds at local time p (performance seconds), or null before a few ticks have come in. */
  at(p: number): number | null { return this.synced ? p - this.off : null; }
  /** Local time (performance seconds) at which the stream shows Chalo time T. */
  localOf(T: number): number | null { return this.synced ? T + this.off : null; }
  /** How much later than the earliest arrival snapshots come in, on the median: network jitter, in ms. */
  get jitterMs() { if (!this.offs.length) return 0; const s = [...this.offs].sort((a, b) => a - b); return (s[s.length >> 1] - s[0]) * 1000; }
}

// ------------------------------------------------------------------ cues from snapshots

type Text = { en: string; hi: string };
export interface Cue { s: SoundId; at?: Vec; delay?: number; loopS?: number; sub?: Text; why: string }
/** What the cues need to know about the world (world px). */
export interface CueWorld {
  timeline: ChaloRules['director']['timeline'];
  mission: string[]; gates: Vec[]; entries: (Vec & { name: string })[]; van: Vec;
  me: Vec | null; metre: number;
}
export interface SnapIn { ph: string; ch: ChaloSnap }
/** A gate that opens this close to you starts the crowd clapping. */
export const NEAR_M = 60;
const CLAP_S = 8;
const CODE = { closed: 0, game: 1, open: 2, back: 3 };

/** The world the cues need, from the Chalo rules and geometry. */
export function cueWorld(R: ChaloRules, G: ChaloGeo, metre: number): CueWorld {
  return { timeline: R.director.timeline, mission: G.cps.map((c) => c.mission), gates: G.cps.map((c) => c.at), entries: G.entries, van: G.van, me: null, metre };
}

/** The sounds that two successive snapshots call for. prev is null for the first one seen. */
export function cuesFor(prev: SnapIn | null, cur: SnapIn, W: CueWorld): Cue[] {
  const out: Cue[] = [], a = prev?.ch, b = cur.ch;
  const sameRound = !!a && a.t <= b.t;
  // the director's timeline: events whose time the clock crossed. A first snapshot late in a round replays nothing
  if (cur.ph === 'playing') {
    const t0 = prev?.ph === 'playing' && sameRound ? a!.t : b.t < 1 ? -1 : b.t - 0.25;
    for (const ev of W.timeline) {
      if (!(t0 < ev.atS && ev.atS <= b.t)) continue;
      if (ev.event === 'metroClosed') {
        const e = W.entries.find((x) => x.name === ev.entry);
        out.push({ s: 'shutter', at: e, why: 'metroClosed' }, { s: 'garble', at: e, delay: 1.6, sub: ev.say, why: 'metroClosed' });
      } else {
        if (ev.event === 'reinforcements' && (ev as { riotToInner?: boolean }).riotToInner) out.push({ s: 'whistleLong', why: 'riotToInner' });
        if (ev.say) out.push({ s: 'garble', at: W.van, sub: ev.say, why: 'say:' + ev.event });
      }
    }
  }
  if (!a || !sameRound) return out;
  // gates: police walk back after an open spell (whistle); a line slides when a gate opens or police walk back (metal)
  const near = (p: Vec) => !!W.me && Math.hypot(p.x - W.me.x, p.y - W.me.y) <= NEAR_M * W.metre;
  b.cs.forEach((row, k) => {
    const was = a.cs[k]?.[0], now = row[0], at = W.gates[k];
    if (was === undefined || was === now) return;
    if (was === CODE.open && now === CODE.back) out.push({ s: 'whistleShort', at, why: 'back' });
    if (now === CODE.open || now === CODE.back) out.push({ s: 'impact', at, why: 'slide' });
    if (now === CODE.open && at && near(at)) out.push({ s: 'clap', at, loopS: CLAP_S, why: 'openNear' });
    if (now === CODE.game && W.mission[k] === 'dharna') out.push({ s: 'clap', at, loopS: CLAP_S, why: 'sitIn' });
  });
  const fell = b.f & ~a.f;
  for (let r = 0; fell >> r; r++) if ((fell >> r) & 1) out.push({ s: 'sting', why: 'ringFalls' });
  if (a.w === -1 && b.w === 1) out.push({ s: 'applause', why: 'win' }, { s: 'win', why: 'win' });
  if (a.w === -1 && b.w === 0) out.push({ s: 'lose', why: 'lose' });
  return out;
}

/** The checkpoint whose Rassakashi game is on (state game), or -1: the dhol plays while there is one. */
export function dholGate(ch: ChaloSnap | null, mission: string[]): number {
  if (!ch) return -1;
  return ch.cs.findIndex((r, k) => r[0] === CODE.game && mission[k] === 'rassakashi');
}
/** The music for a phase: gather, with the extra layer once reinforcements are in; the march after a win; else the debrief. */
export function musicFor(ph: string, ch: ChaloSnap | null, timeline: CueWorld['timeline']): MusicId[] {
  if (!ch) return [];
  if (ph === 'playing') return timeline.some((e) => e.event === 'reinforcements' && e.atS <= ch.t) ? ['gather', 'gatherExtra'] : ['gather'];
  if (ph === 'roundOver') return ch.w === 1 ? ['march'] : ['debrief'];
  return [];
}

// ------------------------------------------------------------------ the engine

interface Store { get(k: string, d: string): string; set(k: string, v: string): void }
interface Live { g: GainNode; p: StereoPannerNode; at: Vec; base: number }
const SUB_MS = 6000;

export class Sounds {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private bus = {} as Record<Bus, GainNode>;
  private buf = new Map<string, AudioBuffer>();
  private missing = new Set<string>();
  private voices = new Map<string, number>();
  private live = new Set<Live>();
  private music = new Map<MusicId, { el: HTMLAudioElement; g: GainNode }>();
  private musicKey = '';
  private crowd: { g: GainNode } | null = null;
  private beds = new Map<string, { el: HTMLAudioElement; g: GainNode }[]>();
  private dhol: { src: AudioBufferSourceNode; g: GainNode; k: number; local: number; T: number } | null = null;
  private prev: SnapIn | null = null;
  private world: CueWorld | null = null;
  private view = { x: 0, y: 0, camX: 0, camZ: 1, W: 0 };
  private sub: (Text & { until: number }) | null = null;
  private glyph = { x: 0, y: 0, s: 0 };
  readonly clock = new ChaloClock();
  muted: boolean;
  /** debug: the last cues, and each dhol start against the beat grid */
  private log: string[] = [];
  private dholLog: { k: number; T: number; errMs: number; local: number }[] = [];

  constructor(private store: Store, readonly disabled = false) {
    this.muted = disabled || store.get('kafila_mute', '0') === '1';
    if (disabled) return;
    const unlock = () => { this.unlock(); for (const ev of ['pointerdown', 'keydown', 'click', 'touchend']) removeEventListener(ev, unlock, true); };
    for (const ev of ['pointerdown', 'keydown', 'click', 'touchend']) addEventListener(ev, unlock, true);
    // menu and HUD buttons click
    document.addEventListener('click', (e) => {
      const b = (e.target as Element | null)?.closest?.('button');
      if (b) this.play(b.id === 'play' || b.id === 'practice' ? 'confirm' : 'click');
    });
  }

  private unlock() {
    if (this.ctx || this.disabled) return;
    try {
      const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!C) return;
      const ctx = new C();
      this.ctx = ctx;
      this.master = ctx.createGain(); this.master.gain.value = this.muted ? 0 : 1; this.master.connect(ctx.destination);
      const level: Record<Bus, number> = { ui: 0.8, sfx: 1, crowd: 0.8, music: 0.45 };
      for (const b of Object.keys(level) as Bus[]) { const g = ctx.createGain(); g.gain.value = level[b]; g.connect(this.master); this.bus[b] = g; }
      ctx.resume().catch(() => { /* resumes on the next gesture */ });
      for (const f of PRELOAD_FILES) this.load(f);
    } catch { this.ctx = null; }
  }

  /** The context and the master, for the announcer's clips (announce.ts); null until the first gesture unlocks sound. */
  voiceOut(): { ctx: AudioContext; node: AudioNode } | null { return this.ctx ? { ctx: this.ctx, node: this.master } : null; }

  private load(f: string): Promise<AudioBuffer | null> {
    if (this.buf.has(f)) return Promise.resolve(this.buf.get(f)!);
    if (this.missing.has(f) || !this.ctx) return Promise.resolve(null);
    this.missing.add(f); // until it arrives: no second fetch
    const ctx = this.ctx;
    return fetch(`/audio/${f}.ogg`).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject()))
      .then((a) => ctx.decodeAudioData(a)).then((b) => { this.buf.set(f, b); this.missing.delete(f); return b; })
      .catch(() => null);
  }

  /** Play a sound now (or after `delay` s); `at` places it in the world. Returns nothing if muted, capped, unloaded or out of earshot. */
  play(id: SoundId, o: { at?: Vec; delay?: number; loopS?: number; gain?: number } = {}): AudioBufferSourceNode | null {
    const ctx = this.ctx, d = SOUNDS[id] as SoundDef;
    if (!ctx || this.muted) return null;
    const file = d.files[Math.floor(Math.random() * d.files.length)], b = this.buf.get(file);
    if (!b) { if (d.lazy) this.load(file); return null; }
    if ((this.voices.get(id) ?? 0) >= d.cap) return null;
    const base = d.gain * (o.gain ?? 1), k = o.at ? this.posK(o.at) : 1;
    if (base * k < 1e-3) return null;
    const src = ctx.createBufferSource(), g = ctx.createGain(), p = ctx.createStereoPanner();
    src.buffer = b; if (d.rate) src.playbackRate.value = d.rate;
    g.gain.value = base * k; p.pan.value = o.at ? this.pan(o.at) : 0;
    src.connect(g).connect(p).connect(this.bus[d.bus]);
    const when = ctx.currentTime + (o.delay ?? 0);
    const lv: Live | null = o.at && o.loopS ? { g, p, at: o.at, base } : null;
    if (o.loopS) { src.loop = true; g.gain.setValueAtTime(base * k, when + o.loopS - 0.6); g.gain.linearRampToValueAtTime(0, when + o.loopS); }
    if (lv) this.live.add(lv);
    this.voices.set(id, (this.voices.get(id) ?? 0) + 1);
    src.onended = () => { this.voices.set(id, Math.max(0, (this.voices.get(id) ?? 1) - 1)); if (lv) this.live.delete(lv); };
    src.start(when);
    if (o.loopS) src.stop(when + o.loopS + 0.05);
    return src;
  }
  private posK(at: Vec) { return posGain(Math.hypot(at.x - this.view.x, at.y - this.view.y) / (this.world?.metre ?? 1)); }
  private pan(at: Vec) { return panFor((at.x - this.view.camX) * this.view.camZ + this.view.W / 2, this.view.W); }

  // hooks for systems not built yet: the press filming, the bus leaving with detainees, the wordless chant
  shutterAt(at: Vec) { this.play('camera', { at }); }
  busLeaves(at: Vec) { this.play('siren', { at }); this.play('busEngine', { at }); }
  chant(at: Vec, forS: number) { this.play('chant', { at, loopS: forS }); }

  /** The Chalo rules and geometry, once loaded. */
  setChalo(R: ChaloRules, G: ChaloGeo, metre: number) { this.world = cueWorld(R, G, metre); }

  /** Each snapshot: ch is the Chalo block (null outside Chalo). */
  snap(ph: string, tk: number, ch: ChaloSnap | null) {
    const W = this.world;
    if (!ch || !W) { this.prev = null; this.setMusic([]); this.setCrowd(0); this.stopDhol(); return; }
    const p = performance.now() / 1000;
    if (ph === 'playing') this.clock.push(tk, ch.t, p);
    const cur = { ph, ch };
    W.me = { x: this.view.x, y: this.view.y };
    for (const c of cuesFor(this.prev, cur, W)) {
      this.play(c.s, { at: c.at, delay: c.delay, loopS: c.loopS });
      if (c.sub) this.sub = { ...c.sub, until: performance.now() + (c.delay ?? 0) * 1000 + SUB_MS };
      this.log.push(c.why + ':' + c.s); if (this.log.length > 30) this.log.shift();
    }
    this.prev = cur;
    this.setMusic(musicFor(ph, ch, W.timeline));
    this.setCrowd(ph === 'playing' && ch.c[1] > 0 ? Math.min(1, ch.c[0] / ch.c[1]) : 0);
    const k = ph === 'playing' ? dholGate(ch, W.mission) : -1;
    if (k >= 0 && !this.dhol) this.startDhol(W.gates[k]);
    else if (k < 0) this.stopDhol();
  }
  /** The platform's bed while you wait: the station's air and a soft track, both looping, faded in and out with it. */
  platformBed(on: boolean) { this.bed('platform', PLATFORM_BED, on); }
  /** The title's bed while the menu is up: its theme, and the city under it. */
  titleBed(on: boolean) { this.bed('title', TITLE_BED, on); }

  /**
   * A bed: looping layers on the music bus, faded in when it starts and out when it stops, apart from the round's music
   * (setMusic), so neither cuts the other. It waits for the first gesture (no context until then).
   */
  private bed(name: string, layers: [MusicId, number][], on: boolean) {
    const ctx = this.ctx, have = this.beds.get(name);
    if (!ctx || on === !!have) return;
    if (on) {
      this.beds.set(name, layers.map(([id, level]) => {
        const el = new Audio(`/audio/${MUSIC[id]}.ogg`); el.loop = true; el.preload = 'auto';
        el.onerror = () => { /* a missing track plays nothing */ };
        const g = ctx.createGain(); g.gain.value = 0;
        ctx.createMediaElementSource(el).connect(g).connect(this.bus.music);
        g.gain.setTargetAtTime(level, ctx.currentTime + 0.1, 1.2);
        el.play().catch(() => { /* not allowed yet, or no file */ });
        return { el, g };
      }));
    } else {
      for (const { el, g } of have!) {
        g.gain.setTargetAtTime(0, ctx.currentTime, 0.5);
        setTimeout(() => { el.pause(); el.removeAttribute('src'); el.load(); }, 2500);
      }
      this.beds.delete(name);
    }
  }

  /** Out of a round (back to the menu): music, crowd and dhol off. */
  idle() { this.snap('lobby', 0, null); }

  /** The dhol loop, started on the AudioContext clock so its beats land on the Chalo clock's 0.6 s grid. */
  private startDhol(at: Vec) {
    const ctx = this.ctx, b = this.buf.get('dhol-loop-100bpm');
    if (!ctx || !b || this.muted) return;
    const now = performance.now() / 1000, T = this.clock.at(now);
    if (T === null) return; // not synced yet: try on the next snapshot
    const beats = Math.round(b.duration / BEAT_S), { k, at: Tk } = nextBeat(T, 0.12);
    const local = this.clock.localOf(Tk)!;
    // the AudioContext time that is heard at that local time (getOutputTimestamp counts the output latency)
    const ts = ctx.getOutputTimestamp?.();
    const when = ts && ts.performanceTime && ts.contextTime !== undefined
      ? ts.contextTime + (local * 1000 - ts.performanceTime) / 1000
      : ctx.currentTime + (local - now) - (ctx.outputLatency || ctx.baseLatency || 0);
    const src = ctx.createBufferSource(), g = ctx.createGain(), p = ctx.createStereoPanner();
    src.buffer = b; src.loop = true; src.loopStart = 0; src.loopEnd = Math.min(b.duration, beats * BEAT_S);
    const base = (SOUNDS.dhol as SoundDef).gain;
    g.gain.value = base * this.posK(at); p.pan.value = this.pan(at);
    src.connect(g).connect(p).connect(this.bus.sfx);
    src.start(Math.max(ctx.currentTime, when), loopOffset(k, beats));
    const lv: Live = { g, p, at, base }; this.live.add(lv);
    src.onended = () => this.live.delete(lv);
    this.dhol = { src, g, k, local, T: Tk };
    this.dholLog.push({ k, T: Tk, local, errMs: 0 }); if (this.dholLog.length > 20) this.dholLog.shift();
  }
  private stopDhol() {
    if (!this.dhol || !this.ctx) { this.dhol = null; return; }
    const { src, g } = this.dhol, t = this.ctx.currentTime;
    g.gain.setTargetAtTime(0, t, 0.08); src.stop(t + 0.5); this.dhol = null;
  }

  private setCrowd(level: number) {
    const ctx = this.ctx;
    if (!ctx) return;
    if (!this.crowd && level > 0) {
      const b = this.buf.get('crowd-shouting-ambience');
      if (!b) return;
      const src = ctx.createBufferSource(), g = ctx.createGain();
      src.buffer = b; src.loop = true; g.gain.value = 0;
      src.connect(g).connect(this.bus.crowd); src.start();
      this.crowd = { g };
    }
    this.crowd?.g.gain.setTargetAtTime(level * (SOUNDS.crowd as SoundDef).gain, ctx.currentTime, 1);
  }

  private setMusic(want: MusicId[]) {
    const ctx = this.ctx, key = want.join();
    if (!ctx || key === this.musicKey) return;
    this.musicKey = key;
    for (const [id, m] of this.music) if (!want.includes(id)) {
      m.g.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
      setTimeout(() => { m.el.pause(); m.el.removeAttribute('src'); m.el.load(); }, 3000);
      this.music.delete(id);
    }
    for (const id of want) if (!this.music.has(id)) {
      try {
        const el = new Audio(`/audio/${MUSIC[id]}.ogg`); el.loop = true; el.preload = 'auto';
        el.onerror = () => { /* a missing track plays nothing */ };
        const g = ctx.createGain(); g.gain.value = 0;
        ctx.createMediaElementSource(el).connect(g).connect(this.bus.music);
        g.gain.setTargetAtTime(1, ctx.currentTime + 0.2, 0.8);
        const main = id === 'gatherExtra' ? this.music.get('gather')?.el : null;
        if (main) el.currentTime = main.currentTime; // the extra layer is the same length: lay it over in step
        el.play().catch(() => { /* not allowed yet, or no file */ });
        this.music.set(id, { el, g });
      } catch { /* no media element */ }
    }
  }

  /** Each frame: where you are (the listener) and the camera, for gains and pans. */
  frame(me: Vec, cam: { x: number; z: number }, W: number) {
    this.view = { x: me.x, y: me.y, camX: cam.x, camZ: cam.z, W };
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    for (const l of this.live) { l.g.gain.setTargetAtTime(l.base * this.posK(l.at), t, 0.1); l.p.pan.setTargetAtTime(this.pan(l.at), t, 0.1); }
  }

  toggle() {
    if (this.disabled) return;
    this.muted = !this.muted; this.store.set('kafila_mute', this.muted ? '1' : '0');
    if (this.ctx) { this.master.gain.setTargetAtTime(this.muted ? 0 : 1, this.ctx.currentTime, 0.05); if (this.muted) this.stopDhol(); else this.play('toggle'); }
  }
  hit(x: number, y: number) { const g = this.glyph; return g.s > 0 && x >= g.x && x <= g.x + g.s && y >= g.y && y <= g.y + g.s; }

  /** The director's line as a subtitle (EN over HI), and the mute glyph top right. Screen px. */
  drawHud(c: CanvasRenderingContext2D, W: number, H: number) {
    const now = performance.now();
    if (this.sub && now > this.sub.until) this.sub = null;
    if (this.sub) {
      const a = Math.min(1, (this.sub.until - now) / 600);
      // the director's line on an asphalt strip: English over Hindi, newsprint type
      c.save(); c.globalAlpha = a; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
      c.font = FONT.body(16, 700); const ew = c.measureText(this.sub.en).width;
      c.font = FONT.body(14, 500); const hw = c.measureText(this.sub.hi).width;
      const bw = Math.max(ew, hw) + 32;
      box(c, W / 2 - bw / 2, H - 100, bw, 54, INK.asphalt, 0, 4);
      c.fillStyle = INK.metro; c.fillRect(W / 2 - bw / 2, H - 100, 5, 54);
      c.fillStyle = INK.newsprint; c.font = FONT.body(16, 700); c.fillText(this.sub.en, W / 2, H - 78);
      c.fillStyle = INK.newsprintDim; c.font = FONT.body(14, 500); c.fillText(this.sub.hi, W / 2, H - 56);
      c.restore();
    }
    // a speaker, with waves when on and a cross when muted
    const s = 26, x = W - 14 - s, y = 46;
    this.glyph = { x, y, s };
    c.save(); box(c, x, y, s, s, this.muted ? INK.newsprint2 : INK.newsprint, 2, 3);
    c.strokeStyle = this.muted ? INK.inkDim : INK.asphalt; c.fillStyle = c.strokeStyle; c.lineWidth = 2; c.lineCap = 'butt';
    c.beginPath(); c.moveTo(x + 5, y + 10); c.lineTo(x + 9, y + 10); c.lineTo(x + 14, y + 5); c.lineTo(x + 14, y + 21); c.lineTo(x + 9, y + 16); c.lineTo(x + 5, y + 16); c.closePath(); c.fill();
    c.beginPath();
    if (this.muted) { c.moveTo(x + 17, y + 10); c.lineTo(x + 22, y + 16); c.moveTo(x + 22, y + 10); c.lineTo(x + 17, y + 16); }
    else { c.arc(x + 14, y + 13, 5, -0.8, 0.8); c.moveTo(x + 14 + 9 * Math.cos(-0.8), y + 13 + 9 * Math.sin(-0.8)); c.arc(x + 14, y + 13, 9, -0.8, 0.8); }
    c.stroke(); c.restore();
  }

  /** For ?debug: state, the last cues, and each dhol start against the beat grid on the current clock estimate. */
  debug() {
    const errNow = (d: { T: number; local: number }) => { const T = this.clock.at(d.local); return T === null ? null : Math.round((T - d.T) * 10000) / 10; };
    return {
      on: !!this.ctx, state: this.ctx?.state ?? 'none', muted: this.muted, disabled: this.disabled, loaded: this.buf.size,
      cues: [...this.log], sub: this.sub?.en ?? null, music: [...this.music.keys()], crowd: this.crowd ? +this.crowd.g.gain.value.toFixed(3) : 0,
      clock: { synced: this.clock.synced, tk0: this.clock.tk0, jitterMs: +this.clock.jitterMs.toFixed(1) },
      dhol: { on: !!this.dhol, starts: this.dholLog.map((d) => ({ k: d.k, T: d.T, errMs: errNow(d) })), outputLatencyMs: this.ctx ? Math.round(((this.ctx.outputLatency || 0) + (this.ctx.baseLatency || 0)) * 1000) : null },
    };
  }
}
